import React, { useEffect, useCallback, useState } from "react";
import { Formik, FormikProps, Form, FormikHelpers } from "formik";
import { Grid, Button, Theme, useTheme, Typography, styled, alpha } from "@mui/material";
// Redux Imports
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState, IAirportOption } from "src/redux/reducers/StructuresReducer";
import { formatIdentFromAirport, updateAirportOptions } from "src/utils/airportHelper";
// Formik Fields
import {
  FormikSelectField,
  FormikDateTimeField,
  FormikTextField,
  FormikCheckboxField,
} from "../../../FormikComponents/FormikWrappers";
import { SwitchField } from "../../../FormikComponents/SwitchField";
import { IPairing, IPositioningFlight } from "@vmosolutions/oc-interfaces";
import { PosFlightDropTarget } from "../PosFlightDropTarget";
//Yup Validation
import * as Yup from "yup";
import { airlineIATAValidation } from "src/components/Admin/Validation/validation";
import { IEmbeddedFormState } from "../PairingEditorWindow";
import _ from "lodash";
import { v1 } from "uuid";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { padNumber } from "src/utils/index";

interface IPositioningFlightFormProps {
  isNew: boolean;
  mosaicPane: HTMLElement;
  positioningFlight: IPositioningFlight;
  updatePositioningFlight: (positioningFlight: IPositioningFlight) => void;
}

// New Non Flying Assignment default values.
export const initialValues: IPositioningFlight = {
  // Required
  _id: undefined,
  flightNumber: undefined,
  outTime: undefined,
  inTime: undefined,
  origin: undefined,
  destination: undefined,
  airlineIATA: undefined,
  requiresBooking: true,
  // Required by handled elsewhere.
  bookings: [],
  isGround: false,
  createdBy: undefined,
  createdAt: undefined,
};

export type IPositioningFlightForm = FormikProps<IPositioningFlight>;

const OCPositioningFlightForm = styled("div")(({ theme }) => ({
  "& .formik_field_container": {
    "& .formik_field": {
      maxWidth: 350,
    },
    "& .pairing_note_textfield": {
      "& .input_control label": { width: 180 },
    },
    "& p.required_fields_label": {
      margin: 0,
      fontWeight: 300,
      fontSize: 10,
      textTransform: "uppercase",
      color: theme.custom.forms.labels,
    },
    "& .required_fields": {
      backgroundColor: alpha(theme.custom.forms.labels, 0.2),
      margin: "2px 0px",
    },
    "& .form_actions": {
      margin: 10,
      "& button": {
        margin: "0px 2px",
        fontSize: 11,
        lineHeight: "12px",
      },
    },
  },
  "& .event_dropzone": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    flexGrow: 1,
    border: `1px dashed ${theme.custom.forms.labels}`,
    backgroundColor: theme.custom.timeline.contextListBackground,
    color: theme.palette.primary.contrastText,
    borderRadius: 3,
    padding: 20,
    marginBottom: "10px",
    "& .title": { fontSize: 18, fontWeight: "bold" },
    "& .body": { fontSize: 14, fontWeight: 300, textAlign: "center" },
  },
}));

//TODO Check why interface has inTime, outTime listed as strings but form has always sent epochs

// PosFlightSchema Form Validation with YUP
const PosFlightSchema = Yup.object().shape({
  flightNumber: Yup.number().required("Flight Number is required"),
  outTime: Yup.number().required("Out-Time is required"),
  inTime: Yup.number().required("In-Time is required"),
  origin: Yup.string().required("Origin is required"),
  destination: Yup.string().required("Destination is required"),
  airlineIATA: Yup.string().matches(airlineIATAValidation, "Must match 2 character airline IATA code"), //required("Airline IATA is required"), - No longer a required field
});

export const PositioningFlightForm = (props: IPositioningFlightFormProps): JSX.Element => {
  const { isNew, mosaicPane, positioningFlight, updatePositioningFlight } = props;
  const { origin, destination } = positioningFlight;
  const dispatch = useDispatch();
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;

  // Default mode to local
  const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(false);
  const [loadingTimezones, setLoadingTimezones] = useState<boolean>(false);

  // Load default values for airport data so they display in the selection menus.
  const loadAirportData = useCallback(
    (icao: string) => {
      updateAirportOptions(icao, airportOptions, dispatch);
    },
    [airportOptions, dispatch]
  );

  // Retrieve initial AirportOptions for origin, destination if not in airport options.
  useEffect(() => {
    if (origin && !airportOptions[origin]) {
      loadAirportData(origin);
      setLoadingTimezones(true);
    }

    if (destination && !airportOptions[destination]) {
      loadAirportData(destination);
      setLoadingTimezones(true);
    }

    if (!!origin && !!airportOptions[origin] && !!destination && !!airportOptions[destination]) {
      setLoadingTimezones(false);
    }
  }, [origin, destination, airportOptions, loadAirportData]);

  const handleSubmit = useCallback(
    (values: IPositioningFlight, helpers: FormikHelpers<IPositioningFlight>): void => {
      if (!values?._id && !values._tempId) {
        // Assign a temp id if no objectid is present (i.e pending creation in draft)
        values._tempId = `temp-${v1()}`;
      }
      // validate in time is after out time
      if (values.inTime < values.outTime) {
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: 'Cannot add/modify positioning flight with "In Time" before "Out Time',
          })
        );
        return;
      } else {
        updatePositioningFlight(values);
        // Reset form on submit to initial values (this is required to ensure more events can be added).
        helpers.resetForm({ values: initialValues });
      }
    },
    [updatePositioningFlight, dispatch]
  );

  return (
    <OCPositioningFlightForm>
      <Formik
        initialValues={positioningFlight}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validationSchema={PosFlightSchema}
        validateOnBlur={true}
      >
        {(formProps: IPositioningFlightForm) => {
          const assignExisting = !!formProps?.values?.flight_id;
          const formTitle =
            formProps?.values?._id || formProps?.values?._tempId
              ? "Edit "
              : assignExisting
                ? "Add Existing Flight"
                : "Add New Positioning Flight";
          const duration =
            formProps?.values?.inTime && formProps?.values?.outTime
              ? Math.abs(formProps?.values?.inTime?.valueOf() - formProps?.values?.outTime?.valueOf()) / 1000
              : undefined;
          const hours = duration ? Math.floor(duration / 3600) : undefined; // need to be able to show 24 hours or greater
          const mins = duration ? Math.floor(duration / 60) % 60 : undefined;

          return (
            <>
              <div style={{ display: "flex", margin: "10px 5px" }}>{formTitle}</div>

              <PosFlightDropTarget formProps={formProps} loadAirportData={loadAirportData}>
                <Form>
                  <SpinnerProgress loading={loadingTimezones} message="loading timezone data" />
                  <Grid container className="formik_field_container" direction="row" wrap="wrap">
                    <p className="required_fields_label">required fields</p>
                    <Grid container className="required_fields">
                      <FormikTextField label="Flight #" name="flightNumber" disabled={assignExisting} type="number" />
                      <FormikSelectField
                        label="Origin"
                        name="origin"
                        disabled={assignExisting}
                        format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                        parse={(airport: IAirportOption) => airport.ident}
                        onInputChange={(ident: string) => {
                          updateAirportOptions(ident, airportOptions, dispatch);
                        }}
                        options={Object.values(airportOptions)}
                        getOptionLabel={(airport: IAirportOption) => `${airport.ident} - ${airport.name}`}
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                        noOptionsMessage={() => "Search airport name or ICAO..."}
                      />
                      <FormikSelectField
                        label="Destination"
                        name="destination"
                        disabled={assignExisting}
                        format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                        parse={(airport: IAirportOption) => airport.ident}
                        onInputChange={(ident: string) => {
                          updateAirportOptions(ident, airportOptions, dispatch);
                        }}
                        options={Object.values(airportOptions)}
                        getOptionLabel={(airport: IAirportOption) => `${airport.ident} - ${airport.name}`}
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                      />
                      <Grid item>
                        {/* DISPLAY DATE/TIME LOCAL UTC SWITCH */}
                        <SwitchField
                          className={`time_mode_selector ${!displayAsUTC && "active"}`}
                          name={"nfaform_time_mode_selector"}
                          value={displayAsUTC}
                          onChange={() => setDisplayAsUTC(!displayAsUTC)}
                          switchlabels={["UTC", "LOCAL"]}
                          switchtooltips={["Times as UTC", "Times as local"]}
                          label="Display Date/Times in"
                          allowNull={false}
                          disabled={!formProps?.values?.origin || !formProps?.values?.destination}
                        />
                      </Grid>
                      <Grid container style={{ width: "auto" }} alignItems="center">
                        <Grid item>
                          <FormikDateTimeField
                            label="Out Time"
                            name="outTime"
                            showAirportTime={formProps?.values?.origin}
                            localizeDisplay={!displayAsUTC}
                            hideLocalizeBtn={true}
                            isEpoch={true}
                            disabled={assignExisting || !formProps?.values?.origin}
                            maxDate={formProps?.values?.inTime?.valueOf()}
                          />
                        </Grid>

                        <Grid item>
                          <FormikDateTimeField
                            label="In Time"
                            name="inTime"
                            showAirportTime={formProps?.values?.destination}
                            localizeDisplay={!displayAsUTC}
                            hideLocalizeBtn={true}
                            isEpoch={true}
                            disabled={assignExisting || !formProps?.values?.destination}
                            minDate={formProps?.values?.outTime?.valueOf()}
                          />
                        </Grid>
                        {typeof hours === "number" && typeof mins === "number" && (
                          <Typography style={{ fontSize: 12, marginLeft: 15 }}>
                            Duration: {`${padNumber(hours, 2)}:${padNumber(mins, 2)}`}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container className="optional_fields">
                      <FormikTextField
                        label="Airline IATA"
                        name="airlineIATA"
                        parse={(value: string) => value?.toUpperCase()}
                      />
                      <Grid item style={{ marginLeft: 15 }}>
                        <FormikCheckboxField
                          className="field checkboxfield"
                          label="Booking Required"
                          name="requiresBooking"
                        />
                      </Grid>

                      {assignExisting && (
                        <FormikTextField label="Timeline Flight Id" name="flight_id" disabled={assignExisting} />
                      )}
                    </Grid>
                    <div className="form_actions">
                      <Button
                        type="reset"
                        variant="contained"
                        color="secondary"
                        disabled={!formProps.dirty}
                        style={{ margin: 5 }}
                      >
                        Reset
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!formProps.dirty || !formProps?.isValid}
                        style={{ margin: 5 }}
                      >
                        {isNew ? "Add" : "Modify"}
                      </Button>
                    </div>
                  </Grid>
                </Form>
              </PosFlightDropTarget>
            </>
          );
        }}
      </Formik>
    </OCPositioningFlightForm>
  );
};
